import { Fragment, memo, useMemo } from 'react';
import { useLocation } from '@tanstack/react-router';
import { navigationItems } from 'src/features/settings/components/SettingsNavItems';
import NavigationTab from 'src/shared/components/ui/navigation/tabs-navigation';
import WithAccessControl from 'src/shared/components/utils/WithAccessControl';
import PageMain from 'src/shared/components/layout/PageMain';
import PageBody from 'src/shared/components/layout/PageBody';
import { ReactChildren } from 'src/shared/types/util.types';
import PageHeaderBar from 'src/shared/components/layout/PageHeaderBar';
import { FEATURE_FLAGS, FeatureFlag } from 'src/shared/constants/featureFlags.constant';
import { useBreadcrumbLevel } from 'src/shared/hooks/useBreadcrumbLevel';
import { Role } from 'src/shared/types/organisation.types';
import { Icons } from 'src/shared/components/ui/icons/dynamic-icon';
import { useOrg } from 'src/shared/contexts/OrgContext';

interface SettingsNavItemProps {
  icon: Icons;
  href: string;
  title: string;
  badge?: string;
  testId?: string;
  isActive: boolean;
}

const SettingsNavItem = memo(function SettingsNavItem({
  icon,
  href,
  title,
  badge,
  testId,
  isActive,
}: SettingsNavItemProps) {
  return <NavigationTab icon={icon} href={href} text={title} badge={badge} testId={testId} isActive={isActive} />;
}, areSettingsNavItemPropsEqual);

function areSettingsNavItemPropsEqual(prev: SettingsNavItemProps, next: SettingsNavItemProps) {
  return (
    prev.isActive === next.isActive &&
    prev.href === next.href &&
    prev.title === next.title &&
    prev.icon === next.icon &&
    prev.badge === next.badge &&
    prev.testId === next.testId
  );
}

const excludeMaxWidthPages = ['/settings/users', '/settings/integrations', '/settings/apikeys', '/settings/audit-logs'];

export function SettingsNav({ children }: ReactChildren) {
  useBreadcrumbLevel(1, 'Settings');
  const { pathname } = useLocation();
  const { role, isSupport, featureFlags } = useOrg();

  const isExcluded = excludeMaxWidthPages.includes(pathname);
  const activeHref = useMemo(() => {
    // ensure stable isActive per item
    const found = navigationItems.find((item) => pathname.startsWith(item.href));
    return found?.href ?? navigationItems[0].href;
  }, [pathname]);

  const visibleItems = useMemo(() => {
    return navigationItems.filter((item) => {
      // Feature-flagged items are shown only if enabled
      if (item.featureFlag && !featureFlags?.has(FEATURE_FLAGS[item.featureFlag as FeatureFlag])) {
        return false;
      }
      // If roles are specified, only show when user is support or role is allowed
      if (item.allowedRoles && item.allowedRoles.length > 0) {
        return isSupport || item.allowedRoles.includes(role as Role);
      }
      // Support-only items: only show to support
      if (item.supportOnly) {
        return isSupport;
      }
      return true;
    });
  }, [featureFlags, isSupport, role]);

  return (
    <PageMain excludeMaxWidth={isExcluded}>
      <PageHeaderBar noBorder />

      <div className="max-w-full px-5 border-b flex ">
        <div className="w-fit translate-y-[1px] flex items-center gap-3">
          {visibleItems.map((item) => {
            const element = (
              <SettingsNavItem
                key={item.href}
                icon={item.icon as Icons}
                href={item.href}
                title={item.title}
                badge={item.badge}
                testId={item.testId}
                isActive={item.href === activeHref}
              />
            );

            const needsAccessControl = !!(item.supportOnly || (item.allowedRoles && item.allowedRoles.length > 0));
            if (needsAccessControl) {
              return (
                <WithAccessControl
                  key={item.href}
                  allowedRoles={item.allowedRoles as Role[] | undefined}
                  renderKey={item.href}
                >
                  {element}
                </WithAccessControl>
              );
            }

            return <Fragment key={item.href}>{element}</Fragment>;
          })}
        </div>
      </div>

      <PageBody>{children}</PageBody>
    </PageMain>
  );
}
